import {ToNumber, ToNumberNullable, ToNumberOrDefault, ToString} from 'lib/utils';
import {useCallback} from 'react';
import {HOME_SAFE} from 'constants/products';
import {useHomeSafeValidation} from 'query/products/home-safe/homesafe-validation';
import {defaultsDeep} from 'lodash';
import {useHomeSafeStandardMetricsQuery} from 'query/products/home-safe/homesafe-standard-metrics';
import {useTranslation} from 'lib/i18n';
import {isLenderCredit} from 'lib/product';

export function useCalculateTierQuery(props) {
  const {t} = useTranslation();

  const {data: tiers = [], defaultTier} = useHomeSafeStandardMetricsQuery({
    ProductId: props?.product?.ProductId,
    State: props?.product?.calculation?.State ?? props?.product?.calculation?.BorrowerProfile?.State,
  });

  const addAdditionalData = useCallback(
    (data, illustration) => {
      const selectedTier =
        tiers.find((tier) => tier.HomeSafeMetricId === illustration.HomeSafeMetricId) ?? defaultTier;

      const HomeSafeMetricId = ToString(selectedTier?.HomeSafeMetricId, defaultTier?.HomeSafeMetricId);
      const Rate = ToNumberOrDefault(selectedTier?.Rate, defaultTier?.Rate);

      return {
        Type: HOME_SAFE,
        HomeSafeType: 'Standard',
        HomeSafeMetricId,
        Rate,
      };
    },
    [defaultTier, tiers],
  );

  const addAdditionalResults = useCallback(
    (data) => {
      const selectedTier =
        tiers.find((tier) => tier.HomeSafeMetricId === data.HomeSafeMetricId) ?? defaultTier;

      const LenderCredit = !!tiers.find((tier) => tier.LenderCredit === data.Rate);

      return {
        LenderCredit,
        State: data.State,
        TotalInitialRate: ToNumber(selectedTier?.Rate),
        HomeSafeMetricId: ToNumber(data.HomeSafeMetricId, ToNumber(defaultTier?.HomeSafeMetricId)),
        showLearnMore: false,
        hideModelImpactButton: false,
        Rate: ToNumberNullable(data.Rate),
        HomeSafeMetric: defaultsDeep(
          {...data?.HomeSafeMetric},
          {
            HomeSafeMetricId: data?.HomeSafeMetric?.HomeSafeMetricId || selectedTier?.HomeSafeMetricId || '',
            Name: data?.HomeSafeMetric?.Name || selectedTier?.Name || '',
            Rate: data?.HomeSafeMetric?.Rate || selectedTier?.Rate || '',
            OriginationFee: selectedTier?.OriginationFee ?? data?.HomeSafeMetric?.OriginationFee ?? '',
          },
        ),
      };
    },
    [tiers, defaultTier],
  );

  const {addProductValidation} = useHomeSafeValidation();

  return {
    enabled: !!defaultTier,
    addAdditionalData,
    addAdditionalResults,
    addProductValidation,
    tiers,
    defaultTier,
    getTierLabel: useCallback(
      (tier = {}, lenderCreditOptions = {}) => {
        let {Rate = 0, OriginationFee = 0, Name = ''} = tier;
        const Fee = OriginationFee * 100;

        if (isLenderCredit(lenderCreditOptions)) {
          Rate = tier.LenderCredit;
        }

        return t('inputsTranslation:HomeSafeMetricId.valueFormat', {
          Name: Name.replace('_', ' '),
          Rate,
          count: Fee,
        });
      },
      [t],
    ),
  };
}
